import { makeStyles } from '@mui/styles';
import Grid from 'apollo-react/components/Grid';
import Loader from 'apollo-react/components/Loader';
import React, { useEffect } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import DashboardHeader from './components/DashboardHeader';
import DashboardOfflinePart from './components/DashboardOfflinePart';
import DashboardPart from './components/DashboardPart';
import DeleteEntityOfflineDataDialog from './components/DeleteEntityOfflineDataDialog';
import EditDashboardPartsDialog from './components/EditDashboardPartsDialog';
import { fetchDashboardConfig } from '../../actions/DashboardConfigActions';
import { fetchOfflineEntities, fetchOfflineSearchResults } from '../../actions/OfflineDataActions';
import Layout from '../../components/Layout';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { useWindowDimensions } from '../../custom-hooks/useWindowDimensions';
import { getNetworkStatus, getTranslations } from '../../form-designer/selectors/common.selectors';
import { getIsOfflineSupportAvailable } from '../../form-designer/selectors/offlineMode.selectors';
import { getDashboardConfig, getMode, getVisibleParts } from '../../selectors/DashboardSelectors';
import { getQsParam } from '../../utils/qs';

const useStyles = makeStyles({
  headerWrapper: {
    padding: '10px',
  },
  dashboardPart: ({ isMobileDevice }) => ({
    paddingLeft: isMobileDevice ? '10px' : '20px',
    paddingRight: '10px',
    marginBottom: '20px',
  }),
  offlineDashboardPart: {
    paddingLeft: '20px',
    paddingRight: '10px',
    marginBottom: '20px',
    marginTop: '20px',
  },
  loaderContainer: {
    height: 'calc(100vh - 64px)',
    position: 'relative',
  },
});

const Dashboard = ({ history, location }) => {
  const { isMobileDevice } = useWindowDimensions();
  const classes = useStyles({ isMobileDevice });
  const dispatch = useDispatch();
  const mode = useSelector(getMode);
  const { isArchiveMode } = useSelector((state) => state.auth);
  const parts = useSelector(getVisibleParts);
  const translations = useSelector(getTranslations);
  const { pending } = useSelector(getDashboardConfig);
  const isOfflineSupportAvailable = useSelector(getIsOfflineSupportAvailable);
  const isOnline = useSelector(getNetworkStatus);
  const translateString = useTranslation();

  useEffect(() => {
    document.title = 'Dashboard';
    if (!isArchiveMode) {
      batch(() => {
        dispatch(fetchDashboardConfig());
        dispatch(fetchOfflineEntities());
        dispatch(fetchOfflineSearchResults());
      });
    }
    return () => {
      sessionStorage.setItem('backButtonURL', location.pathname + location.search);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.title = translateString('Dashboard');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translations.system]);

  useEffect(() => {
    const token = getQsParam('Token');

    if (token) {
      history.replace(location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    <Layout>
      {!isArchiveMode && (
        <>
          {pending ? (
            <div className={classes.loaderContainer}>
              <Loader isInner />
            </div>
          ) : (
            <>
              {isOnline && (
                <div className={classes.headerWrapper}>
                  <DashboardHeader mode={mode} className={classes.mainImg} />
                </div>
              )}
              <Grid container>
                {isOfflineSupportAvailable && !isOnline ? (
                  <Grid item xs={12} lg={4} className={classes.offlineDashboardPart}>
                    <DashboardOfflinePart />
                  </Grid>
                ) : (
                  parts.map((part) => (
                    <Grid
                      key={part.id}
                      item
                      xs={12}
                      lg={part.width}
                      className={classes.dashboardPart}
                    >
                      <DashboardPart mode={mode} part={part} />
                    </Grid>
                  ))
                )}
              </Grid>
            </>
          )}
        </>
      )}
      {mode === 'dashboardParts' && <EditDashboardPartsDialog />}
      <DeleteEntityOfflineDataDialog />
    </Layout>
  );
};

export default Dashboard;
